import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { PageSEO } from '../../components/SEO/PageSEO';
import { ServiceSEO } from '../../components/SEO/ServiceSEO';
import { injectStructuredData } from '../../utils/structured-data';

export default function PrivateAdvisoryProcess() {
  // Inject structured data for the advisory process
  React.useEffect(() => {
    const processSchema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Private Financial Advisory Process at PCM Financial",
      "description": "Our exclusive private advisory process combines sophisticated financial planning with personalized service, delivered wherever and whenever you need it.",
      "image": "https://pcmfinancial.ca/assets/private-advisory-process.jpg",
      "totalTime": "PT2H",
      "supply": {
        "@type": "HowToSupply",
        "name": "Financial Documents",
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": "CAD",
          "value": "0"
        }
      },
      "step": [
        {
          "@type": "HowToStep",
          "name": "Confidential Discovery Session",
          "text": "We begin with a private consultation at your preferred location - your office, home, or via secure video conference. This initial meeting focuses on understanding your unique situation, goals, and concerns.",
          "image": "https://pcmfinancial.ca/assets/step-1-discovery.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "Comprehensive Financial Analysis",
          "text": "Our team conducts a thorough analysis of your current financial position, identifying opportunities for optimization and protection that banks and traditional advisors often miss.",
          "image": "https://pcmfinancial.ca/assets/step-2-analysis.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "Customized Strategy Development",
          "text": "We craft a bespoke financial strategy tailored to your specific needs, incorporating advanced planning techniques typically reserved for ultra-high-net-worth individuals.",
          "image": "https://pcmfinancial.ca/assets/step-3-strategy.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "Private Presentation & Refinement",
          "text": "Your personalized recommendations are presented in a private setting, with ample time for questions, adjustments, and refinements to ensure perfect alignment with your objectives.",
          "image": "https://pcmfinancial.ca/assets/step-4-presentation.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "Seamless Implementation",
          "text": "We handle all implementation details, coordinating with your existing advisors and ensuring a smooth transition with minimal disruption to your busy schedule.",
          "image": "https://pcmfinancial.ca/assets/step-5-implementation.jpg"
        },
        {
          "@type": "HowToStep",
          "name": "Ongoing Private Advisory",
          "text": "Your dedicated advisor provides continuous monitoring, proactive recommendations, and is available whenever you need guidance - not just during business hours.",
          "image": "https://pcmfinancial.ca/assets/step-6-ongoing.jpg"
        }
      ]
    };

    injectStructuredData(processSchema);
  }, []);

  const processSteps = [
    {
      number: "01",
      title: "Confidential Discovery",
      duration: "60-90 minutes",
      description: "Private consultation at your preferred location",
      details: [
        "Understand your unique financial situation",
        "Identify immediate concerns and long-term goals",
        "Review existing coverage and investments",
        "Discuss family and business considerations"
      ]
    },
    {
      number: "02",
      title: "Strategic Analysis",
      duration: "1-2 weeks",
      description: "Comprehensive review by our expert team",
      details: [
        "Tax optimization opportunities",
        "Risk assessment and protection gaps",
        "Estate and succession planning needs",
        "Corporate structure efficiency"
      ]
    },
    {
      number: "03",
      title: "Bespoke Solutions",
      duration: "Tailored to you",
      description: "Custom strategies for your specific needs",
      details: [
        "Access to exclusive products and carriers",
        "Advanced planning techniques",
        "Integration with existing advisors",
        "Flexible implementation options"
      ]
    },
    {
      number: "04",
      title: "Lifetime Partnership",
      duration: "Ongoing",
      description: "Your personal financial quarterback",
      details: [
        "Annual strategy reviews",
        "Proactive tax and legislative updates",
        "Priority access to your advisor",
        "Coordination with your professional team"
      ]
    }
  ];

  const differentiators = [
    {
      traditional: "Office Hours Only",
      private: "Available When You Need Us",
      description: "Evening and weekend availability for busy executives"
    },
    {
      traditional: "Generic Products",
      private: "Exclusive Solutions",
      description: "Access to institutional-grade products and strategies"
    },
    {
      traditional: "Transaction-Focused",
      private: "Relationship-Driven",
      description: "Long-term partnership, not just product sales"
    },
    {
      traditional: "Siloed Advice",
      private: "Integrated Planning",
      description: "Coordination with your CPA, lawyer, and other advisors"
    },
    {
      traditional: "Public Waiting Rooms",
      private: "Complete Discretion",
      description: "Private meetings at your preferred location"
    },
    {
      traditional: "Standard Service",
      private: "White-Glove Experience",
      description: "Concierge-level service tailored to your preferences"
    }
  ];

  return (
    <MarketingLayout>
      <PageSEO
        title="Private Financial Advisory Process | Exclusive Wealth Management"
        description="Discover PCM Financial's exclusive private advisory process. Experience sophisticated financial planning delivered with complete discretion at your convenience."
        keywords={['private financial advisor', 'wealth management process', 'exclusive financial planning', 'private banking alternative']}
      />
      <ServiceSEO
        serviceName="Private Financial Advisory Process"
        serviceDescription="Our exclusive advisory process combines institutional-grade strategies with personalized service, delivered wherever and whenever you need it."
        serviceType="Financial Planning"
        areaServed={['Saskatchewan', 'Alberta', 'Canada']}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container max-w-6xl mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium">
                The Private Advisory Difference
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              A Financial Advisory Process
              <span className="block text-amber-400 mt-2">As Unique As You Are</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Experience sophisticated wealth management delivered with complete discretion,
              on your schedule, at your preferred location.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Begin Your Private Advisory Journey
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Journey to Financial Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A carefully orchestrated process designed to deliver exceptional results
              while respecting your time and privacy
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Connection Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-20 left-full w-full h-0.5 bg-gradient-to-r from-amber-400 to-transparent"></div>
                )}

                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all h-full">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-5xl font-bold text-amber-400">{step.number}</span>
                    <span className="text-sm text-gray-500">{step.duration}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Traditional vs Private Advisory */}
      <section className="py-20 bg-white">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Experience the Private Advisory Advantage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Why successful families and business owners choose private advisory
              over traditional financial services
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-3 gap-4 mb-8">
              <div className="text-center lg:text-left">
                <h3 className="text-lg font-semibold text-gray-500">Traditional Approach</h3>
              </div>
              <div className="hidden lg:block"></div>
              <div className="text-center lg:text-right">
                <h3 className="text-lg font-semibold text-amber-600">Private Advisory</h3>
              </div>
            </div>

            <div className="space-y-6">
              {differentiators.map((item, index) => (
                <div key={index} className="grid lg:grid-cols-3 gap-4 items-center">
                  <div className="bg-white rounded-lg p-4 text-center lg:text-left shadow-sm">
                    <p className="font-medium text-gray-700">{item.traditional}</p>
                  </div>
                  <div className="flex justify-center">
                    <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                  <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 text-center lg:text-right shadow-sm">
                    <p className="font-medium text-amber-900">{item.private}</p>
                  </div>
                  <div className="lg:col-span-3 text-center">
                    <p className="text-sm text-gray-600 italic">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              What You Can Expect
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8">
                <h3 className="text-2xl font-bold text-amber-400 mb-6">Immediate Benefits</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Tax Optimization Strategies</p>
                      <p className="text-gray-400 text-sm">Immediate opportunities to reduce your tax burden</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Protection Gap Analysis</p>
                      <p className="text-gray-400 text-sm">Identify and address critical coverage gaps</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Premium Savings</p>
                      <p className="text-gray-400 text-sm">Access to exclusive rates and products</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8">
                <h3 className="text-2xl font-bold text-amber-400 mb-6">Long-Term Value</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Wealth Preservation</p>
                      <p className="text-gray-400 text-sm">Sophisticated strategies to protect and grow wealth</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Legacy Planning</p>
                      <p className="text-gray-400 text-sm">Ensure your wealth benefits future generations</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Peace of Mind</p>
                      <p className="text-gray-400 text-sm">Know your family and business are protected</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience Private Financial Advisory?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join successful families and business owners who have discovered
            a better way to manage their financial future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-white text-amber-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Schedule Your Private Consultation
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <a
              href="tel:306-861-4995"
              className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white text-white hover:bg-white hover:text-amber-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              Call 306-861-4995
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}